import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, errMsg, useFetch } from "../lib/api";
import { DAYS_LETTER, longDate, monthGrid, thisMonth } from "../lib/fmt";
import { PageHeader, Loading, Segmented, MonthNav } from "../components/bits";

const NEXT = { undefined: "dispo", dispo: "indispo", indispo: undefined };
const CELL = { dispo: "bg-emerald-600 text-white border-emerald-600", indispo: "bg-red-500 text-white border-red-500", undefined: "bg-slate-100 text-slate-600 border-slate-200" };

function MyAvail({ month }) {
  const { sp, refreshBadges } = useApp();
  const { data } = useFetch(sp(`/avail/${month}/me`));
  const [states, setStates] = useState({});
  const [multi, setMulti] = useState(false);
  const [sel, setSel] = useState([]);
  const [saved, setSaved] = useState(false);
  const timer = useRef(null);
  useEffect(() => { if (data) setStates(data.states); }, [data]);
  if (!data) return <Loading />;

  const save = async (dates, state) => {
    try {
      await api.patch(sp(`/avail/${month}/me`), { dates, state: state || null });
      setSaved(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setSaved(false), 1500);
      refreshBadges();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const tap = (d) => {
    if (multi) return setSel((s) => (s.includes(d) ? s.filter((x) => x !== d) : [...s, d]));
    const nx = NEXT[states[d]];
    setStates((s) => { const n = { ...s }; if (nx) n[d] = nx; else delete n[d]; return n; });
    save([d], nx);
  };
  const applyMulti = (state) => {
    setStates((s) => { const n = { ...s }; sel.forEach((d) => { if (state) n[d] = state; else delete n[d]; }); return n; });
    save(sel, state);
    setSel([]);
    setMulti(false);
  };
  const count = Object.values(states).filter((v) => v === "dispo").length;
  return (
    <div data-testid="my-availability">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-lg font-bold text-emerald-700" data-testid="avail-count">{count} jour{count > 1 ? "s" : ""} disponible{count > 1 ? "s" : ""}</span>
        <span className={`text-xs font-semibold text-slate-500 transition-opacity ${saved ? "opacity-100" : "opacity-0"}`} data-testid="avail-saved">✓ Enregistré</span>
      </div>
      <div className="card p-3">
        <div className="mb-2 grid grid-cols-7 text-center text-xs font-bold text-slate-400">{DAYS_LETTER.map((l, i) => <span key={i}>{l}</span>)}</div>
        <div className="grid grid-cols-7 gap-1.5">
          {monthGrid(data.days).map((d, i) => d ? (
            <button key={d} onClick={() => tap(d)} data-testid={`avail-day-${d}`} data-state={states[d] || "none"}
              className={`aspect-square rounded-xl border text-base font-bold transition-colors active:scale-95 ${CELL[states[d]]} ${sel.includes(d) ? "ring-4 ring-teal-400" : ""}`}>
              {Number(d.slice(8))}
            </button>
          ) : <div key={i} />)}
        </div>
      </div>
      <div className="mt-3 flex justify-center gap-4 text-xs font-semibold text-slate-500"><span>🟢 Disponible</span><span>🔴 Indisponible</span><span>⚪ Pas répondu</span></div>
      <div className="mt-5">
        {!multi ? (
          <button className="btn-ghost w-full" onClick={() => setMulti(true)} data-testid="avail-multi-btn">Sélectionner plusieurs jours</button>
        ) : (
          <div className="card space-y-2 p-3" data-testid="avail-multi-panel">
            <p className="text-sm text-slate-500">{sel.length} jour(s) sélectionné(s)</p>
            <div className="grid grid-cols-3 gap-2">
              <button className="btn-primary !px-2 text-sm" disabled={!sel.length} onClick={() => applyMulti("dispo")} data-testid="avail-multi-dispo">Disponible</button>
              <button className="btn-danger !px-2 text-sm" disabled={!sel.length} onClick={() => applyMulti("indispo")} data-testid="avail-multi-indispo">Indispo</button>
              <button className="btn-ghost !px-2 text-sm" disabled={!sel.length} onClick={() => applyMulti(null)} data-testid="avail-multi-clear">Effacer</button>
            </div>
            <button className="w-full py-2 text-sm font-semibold text-slate-500" onClick={() => { setMulti(false); setSel([]); }}>Terminer</button>
          </div>
        )}
        <p className="mt-2 text-center text-xs text-slate-400">Enregistrement automatique à chaque appui.</p>
      </div>
    </div>
  );
}

function TeamAvail({ month }) {
  const { sp } = useApp();
  const { data } = useFetch(sp(`/avail/${month}/team`));
  const [view, setView] = useState("date");
  if (!data) return <Loading />;
  const pending = data.members.filter((m) => !m.submitted);
  const remind = async () => {
    try { const { data: r } = await api.post(sp(`/avail/${month}/remind`)); toast.success(`Rappel envoyé à ${r.sent} personne(s)`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="team-availability">
      <div className="card mb-4 p-4">
        <div className="font-semibold" data-testid="team-answered-count">{data.members.length - pending.length}/{data.members.length} ont répondu</div>
        {pending.length > 0 && (<>
          <div className="mt-1 text-sm text-slate-500">En attente : {pending.map((p) => p.name.split(" ")[0]).join(", ")}</div>
          <button className="btn-soft mt-3 w-full" onClick={remind} data-testid="avail-remind-btn"><BellRing className="h-4 w-4" /> Envoyer un rappel</button>
        </>)}
      </div>
      <div className="mb-4"><Segmented options={[["date", "Par date"], ["member", "Par membre"]]} value={view} onChange={setView} testid="team-view" /></div>
      {view === "member" ? (
        <div className="card divide-y divide-slate-100">
          {data.members.map((m) => (
            <div key={m.user_id} className="flex items-center justify-between px-4 py-3" data-testid={`team-member-${m.user_id}`}>
              <span className="font-semibold">{m.name}</span>
              <span className="text-sm"><b className="text-emerald-700">{m.dispo}</b> 🟢 · <b className="text-red-600">{m.indispo}</b> 🔴{!m.submitted && " · ⚪"}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {data.days.map((d) => (
            <div key={d.date} className="card p-4" data-testid={`team-day-${d.date}`}>
              <div className="flex items-baseline justify-between"><span className="font-bold">{longDate(d.date)}</span>
                <span className={`text-sm font-semibold ${d.dispo.length ? "text-emerald-700" : "text-slate-400"}`}>{d.dispo.length} disponible(s)</span></div>
              {d.dispo.length > 0 && <div className="mt-1 text-sm text-emerald-800">🟢 {d.dispo.map((p) => p.name.split(" ")[0]).join(", ")}</div>}
              {d.indispo.length > 0 && <div className="text-sm text-red-600">🔴 {d.indispo.map((n) => n.split(" ")[0]).join(", ")}</div>}
              {d.noresp.length > 0 && <div className="text-sm text-slate-400">⚪ {d.noresp.map((n) => n.split(" ")[0]).join(", ")}</div>}
            </div>
          ))}
        </div>
      )}
      <p className="mt-4 text-center text-xs text-slate-400">Pour affecter, ouvrez le jour dans Planning.</p>
    </div>
  );
}

export default function Availability() {
  const { isManager, current } = useApp();
  const [params] = useSearchParams();
  const [month, setMonth] = useState(params.get("m") || thisMonth());
  const [tab, setTab] = useState("me");
  const raw = new Date(month + "-01T12:00:00").toLocaleDateString("fr-BE", { month: "long", year: "numeric" });
  const label = raw.charAt(0).toUpperCase() + raw.slice(1);
  const solo = current?.type === "individuel";
  return (
    <div data-testid="availability-page">
      <PageHeader title="Disponibilités" />
      <MonthNav month={month} setMonth={setMonth} label={label} />
      {isManager && !solo && <div className="mb-5"><Segmented options={[["me", "Mes dispos"], ["team", "Équipe"]]} value={tab} onChange={setTab} testid="avail-tab" /></div>}
      {tab === "team" && isManager ? <TeamAvail key={month} month={month} /> : <MyAvail key={month} month={month} />}
    </div>
  );
}
